import { trigger, state, style, transition, animate, keyframes } from '@angular/animations';
import { AfterViewChecked, Component, effect, input, output, inject, signal, ChangeDetectionStrategy, computed } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { faCommentCheck } from '@fortawesome/pro-duotone-svg-icons';
import { faPlay, faRepeat, faSparkles } from '@fortawesome/pro-solid-svg-icons';

import { map } from 'rxjs';

import { MediaService } from '@eiq/files';

import { AIPrompt } from '@empathy/global/audio-chat/models';
import { FileType, ImageTypes, VideoTypes } from '@empathy/global/data';
import { ToPlainText } from '@empathy/global/functions';
import { IFollowUp } from '@empathy/survey/base';
import { IAIPromptForm, AIPromptForm, Question } from '@empathy/survey-core';

import { ContentPipe } from '../../../pipes/content.pipe';
import { SurveyViewerService } from '../../../services/survey-viewer.service';
import { Media } from '@eiq/files/models';

@Component({
  selector: 'empathy-ai-chat-question-view',
  templateUrl: 'ai-chat-question-view.component.html',
  styleUrls: ['./ai-chat-question-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ContentPipe],
  animations: [
    trigger('inOutAnimation', [
      state('in', style({ opacity: 1 })),
      transition(':enter', [
        animate(
          150,
          keyframes([
            style({ opacity: 0, offset: 0 }),
            style({ opacity: 0.25, offset: 0.25 }),
            style({ opacity: 0.5, offset: 0.5 }),
            style({ opacity: 0.75, offset: 0.75 }),
            style({ opacity: 1, offset: 1 })
          ])
        )
      ])
    ])
  ]
})
export class AIChatQuestionViewComponent implements AfterViewChecked {
  question = input<Question>(null);
  questionCtrl = input<FormArray<FormGroup<IAIPromptForm>>>(null);

  forceStop = output<boolean>();
  httpError = output<void>();

  audioContext$ = signal<string>('');
  completed$ = signal(false);
  processingAudio$ = signal(false);
  sendFailed$ = signal(false);
  waiting$ = signal(false);

  faCommentCheck = faCommentCheck;
  faPlay = faPlay;
  faRepeat = faRepeat;
  faSparkles = faSparkles;

  readonly stimulusRaw$ = computed<Media>(() => {
    if (!this.question() || this.question().stimulus?.length === 0) {
      return null;
    }
    return this.question().stimulus[0];
  });

  private chatReference: string;
  private failedRequest: AIPrompt;
  private contentPipe = inject(ContentPipe);
  private surveyViewerService = inject(SurveyViewerService);

  constructor() {
    effect(
      () => {
        if (this.question().requiredInd) {
          this.forceStop.emit(true);
        }

        const isChatCompleted =
          this.questionCtrl().length > 0 &&
          this.questionCtrl()
            .at(this.questionCtrl().length - 1)
            .get('completed')?.value;

        if (isChatCompleted) {
          this.finishChat();
        }

        if (this.questionCtrl().length === 0) {
          this.questionCtrl().push(new AIPromptForm(new AIPrompt(this.question().startingQuestion)).form);
          this.audioContext$.set(this.question().startingQuestion);
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  onAnswer(answerText: string): void {
    const promptAnswered = this.getQuestionTextForFollowUp();

    promptAnswered.get('answerText').setValue(answerText);
    // promptAnswered.get('answerAudio').setValue(null);

    const aiPromptAnswer = structuredClone(promptAnswered.value) as AIPrompt;

    this.processAnswer(aiPromptAnswer);
  }

  // onAnswerAudio(audioRequest: { audio: Blob; transcription: string }): void {
  //   const promptAnswered = this.questionCtrl().at(this.questionCtrl().length - 1);
  //   promptAnswered.get('answerAudio').setValue(audioRequest.audio);
  //   this.onAnswer(audioRequest.transcription);
  // }

  onResend(): void {
    this.requestFollowUp(this.failedRequest);
  }

  private scrollToBottom(): void {
    const pageContainer = document.getElementById('page-view__container');
    try {
      pageContainer.scrollTop = pageContainer.scrollHeight;
    } catch (err) {}
  }

  private getQuestionTextForFollowUp(): FormGroup<IAIPromptForm> {
    const promptAnswered = this.questionCtrl().at(this.questionCtrl().length - 1);
    if (this.questionCtrl().length === 1) {
      promptAnswered.get('questionText').setValue(this.contentPipe.transform(this.question().startingQuestion));
    }

    return promptAnswered;
  }

  private processAnswer(followupRequest: AIPrompt): void {
    if (this.questionCtrl().length === this.question().promptsNumber + 1) {
      this.questionCtrl().push(this.mapFinalMessage('Thank you for your answers. You can continue with the survey now.'));
      this.finishChat();

      return;
    }

    if (!this.surveyViewerService.aiFollowupCall) {
      this.questionCtrl().push(this.mapFinalMessage('This question type cannot be tested from the question preview. Go to Full Preview to test it.'));
      return;
    }

    followupRequest.questionId = this.question().id;
    followupRequest.referenceId = this.chatReference;
    followupRequest.questionText = ToPlainText(followupRequest.questionText);

    this.requestFollowUp(followupRequest);
  }

  private requestFollowUp(followupRequest: AIPrompt): void {
    this.waiting$.set(true);
    this.sendFailed$.set(false);

    this.surveyViewerService.aiFollowupCall(followupRequest).subscribe((followUp: IFollowUp) => {
      this.waiting$.set(false);
      if (!followUp) {
        this.sendFailed$.set(true);
        this.failedRequest = followupRequest;
        return;
      }

      if (!followUp || !followUp.followupInd) {
        this.questionCtrl().push(this.mapFinalMessage('Thank you for your answers. You can continue with the survey now.'));
        this.finishChat();
        return;
      }

      if (!this.chatReference) {
        this.chatReference = followUp.referenceId;
      }

      this.questionCtrl().push(new AIPromptForm(new AIPrompt(followUp.followupQuestionText)).form);

      this.audioContext$.set(followUp.followupQuestionText);
    });
  }

  private mapFinalMessage(text: string): FormGroup<IAIPromptForm> {
    return new AIPromptForm(new AIPrompt(text, true)).form;
  }

  private finishChat(): void {
    this.waiting$.set(false);
    this.sendFailed$.set(false);
    this.completed$.set(true);
    this.forceStop.emit(false);
  }
}
